import {toast} from 'react-toastify';
import {format, formatDistanceToNow} from 'date-fns';
import {observer} from 'mobx-react-lite';
import React, {FC, useState} from 'react';
import {ArrowPathIcon, KeyIcon, TrashIcon} from '@heroicons/react/24/outline';
import {deleteApiKey, IApiKey, rotateApiKey, VerdocsEndpoint} from '@verdocs/js-sdk';
import {CreateApiKeyDialog, CreatedApiKeyDialog, OkCancelDialog} from '../../Dialogs';
import {invalidateQuery, useApiKeys} from '../../react-sdk/queries';
import {Button, FlexFill, HelpDialog, PageTitle, Spinner} from '../../Components';
import {AppState} from '../../AppState';
import {McpTokensSection} from './McpTokens';

const Card: FC<{children: React.ReactNode; className?: string}> = ({children, className = ''}) => (
  <div className={`rounded-lg border border-[#ebecf0] bg-white p-5 ${className}`}>{children}</div>
);

const SectionTitle: FC<{children: React.ReactNode}> = ({children}) => (
  <div className="mb-3 text-[15px] font-medium text-[#33364b]">{children}</div>
);

/**
 * Global-admin keys override their profile's permissions, so they read as a warning (amber). A
 * normal key acts as one profile and is labelled with whose, in brand purple.
 */
const AccessBadge: FC<{apikey: IApiKey}> = ({apikey}) => {
  if (apikey.global_admin) {
    return <span className="rounded-full bg-[#fdf1e3] px-2 py-0.5 text-[11px] font-medium text-[#95591a]">Global Admin</span>;
  }

  const owner = [apikey.profile?.first_name, apikey.profile?.last_name].filter(Boolean).join(' ');
  return (
    <span className="rounded-full bg-[#efedfb] px-2 py-0.5 text-[11px] font-medium text-[#654dcb]">
      {owner ? `Personal: ${owner}` : 'Personal'}
    </span>
  );
};

const ICON_BUTTON =
  'rounded-md p-2 text-gray-400 transition-colors hover:bg-gray-100 disabled:cursor-default disabled:opacity-40 tablet:p-1.5 [&>svg]:h-5 [&>svg]:w-5 tablet:[&>svg]:h-4 tablet:[&>svg]:w-4';

/**
 * Two layouts: below tablet the name sits on its own line with the badge and actions spread on a second
 * line; from tablet up it is one row. created_at and last_used_at may be absent until the API rollout
 * completes, so both are optional here.
 */
const KeyRow: FC<{apikey: IApiKey; onRotate: () => void; onDelete: () => void; disabled: boolean}> = ({
  apikey,
  onRotate,
  onDelete,
  disabled,
}) => (
  <div className="flex flex-col gap-2 border-b border-[#f2f3f5] py-3 last:border-0 tablet:flex-row tablet:items-center tablet:gap-3">
    <div className="min-w-0 flex-1">
      <div className="truncate text-[13px] font-medium text-[#33364b]">{apikey.name}</div>
      {apikey.client_id && (
        <div className="mt-0.5 truncate font-mono text-[11.5px] text-gray-400" title={apikey.client_id}>
          {apikey.client_id}
        </div>
      )}
    </div>

    {/* tablet:contents dissolves this wrapper so its children become cells of the single row. */}
    <div className="flex items-center justify-between gap-3 tablet:contents">
      <div className="shrink-0 tablet:w-[190px]">
        <AccessBadge apikey={apikey} />
      </div>

      <div className="hidden w-[150px] shrink-0 text-[12px] text-gray-500 desktop:block">
        {apikey.last_used_at === null ? (
          <span className="text-gray-400">Never used</span>
        ) : apikey.last_used_at ? (
          <>Used {formatDistanceToNow(new Date(apikey.last_used_at), {addSuffix: true})}</>
        ) : null}
        {apikey.created_at && <div className="text-gray-400">Created {format(new Date(apikey.created_at), 'MMM d, yyyy')}</div>}
      </div>

      <div className="flex shrink-0 gap-1">
        <button onClick={onRotate} disabled={disabled} title="Rotate secret" className={`${ICON_BUTTON} hover:text-[#654dcb]`}>
          <ArrowPathIcon />
        </button>
        <button onClick={onDelete} disabled={disabled} title="Delete key" className={`${ICON_BUTTON} hover:text-[#c2410c]`}>
          <TrashIcon />
        </button>
      </div>
    </div>
  </div>
);

export const ApiKeys: FC = observer(() => {
  const organizationId = AppState.currentProfile?.organization_id || '';
  const apiKeysQuery = useApiKeys(organizationId);
  const apiKeys = apiKeysQuery.data || [];

  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState<{apikey: IApiKey; rotated: boolean} | null>(null);
  const [confirmingRotate, setConfirmingRotate] = useState<IApiKey | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<IApiKey | null>(null);
  const [working, setWorking] = useState(false);

  const handleCreated = (apikey: IApiKey) => {
    setCreating(false);
    setRevealed({apikey, rotated: false});
    invalidateQuery(['api_keys']);
  };

  const handleRotate = () => {
    const target = confirmingRotate;
    setConfirmingRotate(null);
    if (!target) {
      return;
    }

    setWorking(true);
    rotateApiKey(VerdocsEndpoint.getDefault(), target.client_id)
      .then((r) => {
        setWorking(false);
        setRevealed({apikey: r, rotated: true});
        invalidateQuery(['api_keys']);
      })
      .catch((e) => {
        setWorking(false);
        console.log('Error rotating key', e);
        toast.error('Unable to rotate key, please try again later.');
      });
  };

  const handleDelete = () => {
    const target = confirmingDelete;
    setConfirmingDelete(null);
    if (!target) {
      return;
    }

    setWorking(true);
    deleteApiKey(VerdocsEndpoint.getDefault(), target.client_id)
      .then(() => {
        setWorking(false);
        toast.success('The API key has been deleted.');
        invalidateQuery(['api_keys']);
      })
      .catch((e) => {
        setWorking(false);
        console.log('Delete error', e);
        toast.error('There was an error deleting the API key. Please try again later.');
      });
  };

  return (
    <div className="w-full max-w-[860px] pb-10">
      <PageTitle>
        {'API Keys & MCP'}
        <FlexFill />
        <HelpDialog title="API Keys & MCP">
          <p>
            API keys authenticate server-to-server calls to the Verdocs API. A key acts on behalf of your organization, so treat it like
            a password: keep it in server-side configuration, never in a browser or mobile app, and delete keys you no longer recognize.
          </p>
          <p>
            MCP tokens connect AI assistants to your account. A token acts as you, with the scopes you choose, and can be revoked here at
            any time.
          </p>
        </HelpDialog>
      </PageTitle>

      {apiKeysQuery.isLoading ? (
        <Spinner mode="dark" />
      ) : apiKeys.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[#dcdee5] bg-[#fbfbfc] p-8 text-center">
          <div className="text-[15px] font-medium text-[#33364b]">No API keys yet</div>
          <div className="mx-auto mt-1 max-w-[440px] text-[13px] text-gray-500">
            API keys let your own systems create envelopes, pull status, and manage templates without a person signing in. You will see
            the secret once, when the key is created.
          </div>
          <div className="mt-4 flex justify-center">
            <Button label="Create a key" onClick={() => setCreating(true)} />
          </div>
        </div>
      ) : (
        <>
          <Card className="mb-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <SectionTitle>
                {apiKeys.length} {apiKeys.length === 1 ? 'key' : 'keys'}
              </SectionTitle>
              <Button size="small" label="Create key" icon={<KeyIcon className="h-4 w-4" />} onClick={() => setCreating(true)} />
            </div>

            {apiKeys.map((apikey) => (
              <KeyRow
                key={apikey.client_id}
                apikey={apikey}
                disabled={working}
                onRotate={() => setConfirmingRotate(apikey)}
                onDelete={() => setConfirmingDelete(apikey)}
              />
            ))}
          </Card>

          <div className="rounded-lg border border-[#f0d9b5] bg-[#fdf8ef] p-5">
            <div className="mb-2 text-[15px] font-medium text-[#95591a]">Keeping keys safe</div>
            <div className="text-[13px] leading-normal text-[#95591a]">
              API keys are for server-to-server calls, and should never be exposed in front-end (Web or mobile) code. Secrets are only
              displayed when a key is created or rotated. If you lose a secret or it is compromised, you can rotate it to generate a new
              one. API keys should never be shared, and Verdocs support will never ask you for one.
            </div>
          </div>
        </>
      )}

      <div className="my-8 flex items-center gap-3">
        <div className="h-px flex-1 bg-[#ebecf0]" />
        <span className="text-[11px] font-medium uppercase tracking-wider text-[#9b9ea8]">MCP</span>
        <div className="h-px flex-1 bg-[#ebecf0]" />
      </div>

      <McpTokensSection />

      {creating && <CreateApiKeyDialog onCreated={handleCreated} onClose={() => setCreating(false)} />}

      {revealed && (
        <CreatedApiKeyDialog
          apikey={revealed.apikey}
          rotated={revealed.rotated}
          onClose={() => {
            setRevealed(null);
            invalidateQuery(['api_keys']);
          }}
        />
      )}

      {confirmingRotate && (
        <OkCancelDialog onOk={handleRotate} onCancel={() => setConfirmingRotate(null)}>
          <p className="text-[13px] text-gray-500">
            Rotating this secret will invalidate the old value immediately. Continue?
          </p>
        </OkCancelDialog>
      )}

      {confirmingDelete && (
        <OkCancelDialog onOk={handleDelete} onCancel={() => setConfirmingDelete(null)}>
          <p className="text-[13px] text-gray-500">
            Deleting <span className="font-medium text-[#33364b]">{confirmingDelete.name}</span> stops anything using it immediately, and
            the key cannot be restored.
            {confirmingDelete.last_used_at
              ? ` This key was last used ${formatDistanceToNow(new Date(confirmingDelete.last_used_at), {addSuffix: true})}.`
              : ''}
          </p>
        </OkCancelDialog>
      )}
    </div>
  );
});
